<template>
  <div class="compare-chart">
    <chart-toolbar
      :locked="!!lockedY"
      :zoomed="!!zoomRange"
      :lockable="!!tkCurrentDomains"
      @lock="tkToggleLockY(tkCurrentDomains)"
      @reset="tkPublishZoom(null)"
      @png="tkExportPng(fileBase)"
      @csv="exportCsv"
    />
    <div v-if="!timestamps.length" class="compare-chart__empty">No data for this range.</div>
    <div v-show="timestamps.length" ref="chartContainer" class="compare-chart__canvas"></div>
    <div ref="tooltip" class="compare-chart__tooltip"></div>
    <div class="compare-chart__legend">
      <button
        v-for="ds in datasets"
        :key="ds.id"
        type="button"
        class="compare-chart__legend-item"
        :class="{ 'is-hidden': hiddenSeries[ds.name] }"
        @click="tkToggleSeries(ds.name)"
      >
        <span class="compare-chart__swatch" :style="{ background: ds.color }"></span>
        <span>{{ ds.name }} ({{ ds.unit }})</span>
        <span v-if="statusText(ds.status)" class="compare-chart__status">{{ statusText(ds.status) }}</span>
      </button>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import { formatDateTime } from '@/util/date';
import { CHART_TOOLTIP } from '@/util/apiDatetime';
import { formatNum } from '@/util/number';
import { escapeHtml } from '@/util/chart/customChartModel';
import chartToolkit from '@/components/charts/chartToolkit';
import ChartToolbar from '@/components/charts/ChartToolbar.vue';

const STATUS_TEXT = {
  empty: '(no data)', failed: '(failed)', truncated: '(too much data)', missing: '(vessel not found)',
};
const AXIS_GAP = 56;
const DOT_LIMIT = 300;

export default {
  name: 'CompareChart',
  mixins: [chartToolkit],
  components: { ChartToolbar },
  props: {
    timestamps: { type: Array, default: () => [] },
    datasets: { type: Array, default: () => [] },
    xExtent: { type: Array, default: null },
    syncGroup: { type: String, default: '' },
    tkKeyExtra: { type: String, default: '' },
    fileBase: { type: String, default: 'custom-chart' },
    fileRange: { type: Array, default: null },
    longRange: { type: Boolean, default: false },
  },
  data() {
    return {
      resizeObserver: null,
      tkCurrentDomains: null,
    };
  },
  created() {
    this.tkLast = null;
    this.lastWidth = 0;
  },
  mounted() {
    this.tkBind();
    this.lastWidth = this.$el.clientWidth;
    this.drawChart();
    // Gambar ulang cuma kalau lebarnya beneran berubah.
    this.resizeObserver = new ResizeObserver(() => {
      const width = this.$el.clientWidth;
      if (width === this.lastWidth) return;
      this.lastWidth = width;
      this.drawChart();
    });
    this.resizeObserver.observe(this.$el);
  },
  beforeDestroy() {
    this.tkUnbind();
    if (this.resizeObserver) this.resizeObserver.disconnect();
  },
  watch: {
    // Tunggu v-show kanvasnya kebuka dulu, kalau enggak lebarnya kebaca 0.
    datasets() { this.$nextTick(this.drawChart); },
  },
  methods: {
    statusText(status) {
      return STATUS_TEXT[status] || '';
    },
    tkFileRange() {
      return this.fileRange;
    },
    exportCsv() {
      if (this.tkLast) this.tkExportCsv(this.fileBase, this.tkLast.timestamps, this.tkLast.datasets);
    },
    drawChart() {
      const container = this.$refs.chartContainer;
      if (!container) return;
      d3.select(container).selectAll('*').remove();
      this.tkCurrentDomains = null;
      this.tkLast = null;
      if (!this.timestamps.length) return;

      const timestamps = this.timestamps.map((ms) => new Date(ms));
      // domainFor baca `interpolated`; di sini sama dengan data asli (celah tetap celah).
      const all = this.datasets.map((d) => ({ ...d, interpolated: d.data }));
      const visible = this.tkVisible(all);
      // Posisi sumbu dari semua seri yang punya data, biar gak loncat pas legend diklik.
      const units = [];
      all.forEach((d) => { if (d.status === 'ok' && d.axis && !units.includes(d.axis)) units.push(d.axis); });

      const outerWidth = Math.max(this.$el.clientWidth || container.clientWidth, 320);
      const compact = outerWidth < 576;
      const margin = {
        top: 12,
        right: 16 + (units.length > 1 ? 44 : 0) + (units.length > 3 ? AXIS_GAP : 0),
        bottom: 48,
        left: 52 + (units.length > 2 ? AXIS_GAP : 0),
      };
      const width = Math.max(1, outerWidth - margin.left - margin.right);
      const height = compact ? 260 : 320;
      const tickCount = compact ? 4 : 6;

      const svg = d3.select(container).append('svg')
        .attr('width', outerWidth)
        .attr('height', height + margin.top + margin.bottom)
        .append('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

      // Tanpa ini garis meluber ke luar kotak pas sumbu dikunci.
      const clipId = `cc-clip-${this._uid}`;
      svg.append('defs').append('clipPath').attr('id', clipId)
        .append('rect')
        .attr('width', width)
        .attr('height', height);
      const dataLayer = svg.append('g').attr('clip-path', `url(#${clipId})`);

      const extent = this.xExtent ? this.xExtent.map((ms) => new Date(ms)) : timestamps;
      const x = d3.scaleTime().domain(this.tkXDomain(extent)).range([0, width]);
      const xFormat = this.longRange ? 'DD MMM HH:mm' : 'HH:mm';
      svg.append('g').attr('transform', `translate(0,${height})`)
        .call(d3.axisBottom(x).ticks(compact ? 4 : 8).tickFormat((d) => formatDateTime(d, xFormat, null, false)));

      // Domain mentah dulu, baru garis nolnya disejajarkan.
      const aligned = this.tkAlignZero(units.map((u) => this.tkDomain(u, visible, 1)));
      const scales = {};
      const domains = {};
      units.forEach((unit, i) => {
        const [lo, hi] = aligned[i];
        domains[unit] = aligned[i];
        scales[unit] = d3.scaleLinear().domain([lo, hi + (hi - lo) * 0.05]).range([height, 0]);
      });
      this.tkCurrentDomains = units.length ? domains : null;

      // Angka besar disingkat biar gak kepotong margin.
      const tickFormat = (scale) => {
        const base = scale.tickFormat(tickCount);
        return (v) => (Math.abs(v) >= 10000 ? d3.format('.3~s')(v) : base(v));
      };

      // Satuan ke-1 kiri, ke-2 kanan, ke-3 kiri kedua, ke-4 kanan kedua.
      const axisX = [0, width, -AXIS_GAP, width + AXIS_GAP];
      units.forEach((unit, i) => {
        const onLeft = i % 2 === 0;
        const scale = scales[unit];
        const axis = (onLeft ? d3.axisLeft(scale) : d3.axisRight(scale)).ticks(tickCount).tickFormat(tickFormat(scale));
        const color = (all.find((d) => d.axis === unit && d.status === 'ok') || {}).color || '#333';
        svg.append('g').attr('transform', `translate(${axisX[i]},0)`)
          .call(axis)
          .append('text')
          .attr('fill', color)
          .attr('x', onLeft ? -8 : 8)
          .attr('y', -2)
          .style('text-anchor', onLeft ? 'end' : 'start')
          .style('font-weight', '600')
          .text(unit);
      });

      if (units.length) {
        svg.append('g').attr('class', 'grid').attr('opacity', 0.1)
          .call(d3.axisLeft(scales[units[0]]).ticks(tickCount).tickSize(-width).tickFormat(''));
      }

      visible.forEach((ds, index) => {
        const y = scales[ds.axis];
        if (!y) return;
        dataLayer.append('path')
          .datum(ds.data)
          .attr('fill', 'none')
          .attr('stroke', ds.color)
          .attr('stroke-width', 2)
          .attr('d', d3.line().defined((v) => v !== null).x((v, i) => x(timestamps[i])).y((v) => y(v)));
        const points = ds.data.map((v, i) => ({ v, t: timestamps[i] })).filter((p) => p.v !== null);
        // Titik tunggal di antara celah gak kelihatan kalau cuma digambar garis.
        if (points.length <= DOT_LIMIT) {
          dataLayer.selectAll(`.cc-dot-${index}`)
            .data(points)
            .enter()
            .append('circle')
            .attr('class', `cc-dot-${index}`)
            .attr('cx', (p) => x(p.t))
            .attr('cy', (p) => y(p.v))
            .attr('r', 2.5)
            .attr('fill', ds.color)
            .style('pointer-events', 'none');
        }
      });

      // Ekspor ngikut yang keliatan: kena zoom dan seri yang disembunyiin.
      const [xFrom, xTo] = x.domain();
      const shown = [];
      timestamps.forEach((t, i) => { if (t >= xFrom && t <= xTo) shown.push(i); });
      this.tkLast = {
        timestamps: shown.map((i) => timestamps[i]),
        datasets: visible.map((d) => ({ name: `${d.name} (${d.unit})`, data: shown.map((i) => d.data[i]) })),
      };

      const tooltip = d3.select(this.$refs.tooltip);
      const bisect = d3.bisector((d) => d).left;
      const focus = svg.append('g').style('display', 'none');
      const focusLine = focus.append('line').attr('stroke', '#aaa').attr('stroke-dasharray', '3 3')
        .attr('y1', 0)
        .attr('y2', height);
      const focusDots = visible.map((ds) => focus.append('circle').attr('r', 5).attr('fill', ds.color)
        .attr('stroke', '#fff')
        .attr('stroke-width', 2));

      this.tkOnHover((ms) => {
        const [first, last] = x.domain();
        if (ms === null || ms < +first || ms > +last) {
          focus.style('display', 'none');
          return;
        }
        focus.style('display', null);
        focusLine.attr('x1', x(new Date(ms))).attr('x2', x(new Date(ms)));
      });

      const hoverBase = this.tkInstallBrush(svg, x, width, height);
      hoverBase
        .on('mouseover', () => {
          tooltip.style('opacity', 1);
          focus.style('display', null);
        })
        .on('mouseout', () => {
          tooltip.style('opacity', 0);
          focus.style('display', 'none');
          this.tkPublishHover(null);
        })
        .on('mousemove', (event) => {
          const x0 = x.invert(d3.pointer(event)[0]);
          const i = bisect(timestamps, x0, 1);
          let index = 0;
          if (i > 0 && i < timestamps.length) index = x0 - timestamps[i - 1] > timestamps[i] - x0 ? i : i - 1;
          else if (i >= timestamps.length) index = timestamps.length - 1;
          const date = timestamps[index];
          const xPos = x(date);
          focusLine.attr('x1', xPos).attr('x2', xPos);
          this.tkPublishHover(+date);

          let html = `<strong>${formatDateTime(date, CHART_TOOLTIP)}</strong>`;
          visible.forEach((ds, idx) => {
            const value = ds.data[index];
            const y = scales[ds.axis];
            if (value === null || value === undefined || !y) {
              focusDots[idx].style('display', 'none');
              return;
            }
            focusDots[idx].attr('cx', xPos).attr('cy', y(value)).style('display', null);
            const real = ds.shiftMs
              ? ` <span style="opacity:.7">(${formatDateTime(ds.realTs[index], CHART_TOOLTIP)})</span>`
              : '';
            html += `<div style="display:flex;align-items:center;gap:5px;"><span style="width:8px;height:8px;border-radius:50%;background:${escapeHtml(ds.color)};"></span>${escapeHtml(ds.name)}: ${formatNum(value, 2)} ${escapeHtml(ds.unit)}${real}</div>`;
          });

          const [posX, posY] = d3.pointer(event, container);
          let left = posX + 15;
          if (left + 260 > outerWidth) left = posX - 270;
          tooltip.html(html).style('left', `${left}px`).style('top', `${posY + 15}px`);
        });
    },
  },
};
</script>

<style scoped>
.compare-chart { position: relative; font-family: inherit; }
/* d3-axis nempelin font-family="sans-serif" di grup axis; CSS menang atas atribut itu. */
.compare-chart ::v-deep [font-family] { font-family: inherit; }
.compare-chart__canvas { width: 100%; }
.compare-chart__empty { padding: 48px 0; text-align: center; color: #6b7280; font-size: 13px; }
.compare-chart__tooltip {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  padding: 8px;
  border-radius: 4px;
  font-size: 12px;
  z-index: 10;
  max-width: 260px;
}
.compare-chart__legend { display: flex; flex-wrap: wrap; gap: 6px 14px; padding: 4px 4px 0; }
.compare-chart__legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  background: transparent;
  padding: 2px 0;
  font-size: 12px;
  color: #374151;
  cursor: pointer;
}
.compare-chart__legend-item.is-hidden { opacity: 0.4; text-decoration: line-through; }
.compare-chart__swatch { width: 12px; height: 12px; border-radius: 2px; flex-shrink: 0; }
.compare-chart__status { color: #b45309; }
</style>
